import { useEffect, useSyncExternalStore } from 'react';
import { useEmpathyQueueStore } from '../../stores/empathy-queue.store';
import { isLiveSessionPath } from '../../utils/liveSessionPath';
import { useFullScreenStageOpen } from '../stage/fullScreenStage';

/**
 * "DON'T INTERRUPT NOW": when the app's own notices (the circle-starting-soon
 * notice today) wait instead of appearing. One module, so every quiet rule
 * lives in one place (docs/plans/CIRCLE-STARTING-NOTICE.md §6, class D).
 *
 * Two kinds of signal:
 *
 *  - DECLARED: a surface says "quiet while I am up" with useQuietAppNotices()
 *    (or by rendering <QuietAppNotices />): an oral quiz, an Echo Arena round
 *    with the mic open, Empy recording, the 1:1 mic check, a ring or
 *    incoming-call sheet, the availability toast.
 *  - DERIVED: signals that already exist, read here: a live circle route
 *    (isLiveSessionPath), a full-screen 1:1 stage, and the empathy queue
 *    states that are one tap from a call.
 *
 * A notice that waits is not "shown", so it is not used up.
 *
 * REF-COUNTED like fullScreenStage.ts: React StrictMode mounts an effect,
 * tears it down and mounts it again, and a boolean would be left false.
 */
let declared = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Declare quiet. Returns the release, safe to call more than once. */
export function declareQuietAppNotices(): () => void {
  declared += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    declared -= 1;
    emit();
  };
}

/** Quiet for as long as the calling component is mounted and `active` is true. */
export function useQuietAppNotices(active = true): void {
  useEffect(() => {
    if (!active) return undefined;
    return declareQuietAppNotices();
  }, [active]);
}

/** The same, as an element, for hosts that decide after their hooks have run. */
export function QuietAppNotices(): null {
  useQuietAppNotices(true);
  return null;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return declared > 0;
}

/** Outside React (tests, diagnostics). */
export function isQuietDeclared(): boolean {
  return getSnapshot();
}

/** Giver states one tap from a call (decided: suppress). */
export const QUIET_GIVER_STATES: ReadonlySet<string> = new Set([
  'CHECKING_IN',
  'WAITING',
  'PINGED',
  'ACTIVE',
]);

/** Seeker states being matched or in a call. */
export const QUIET_SEEKER_STATES: ReadonlySet<string> = new Set([
  'WAITING',
  'MATCHING',
  'CONNECTED',
  'FALLBACK_ACTIVITY',
]);

/** Every quiet rule, declared and derived, for this route. */
export function useAppNoticesQuiet(pathname: string): boolean {
  const isDeclared = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const stageOpen = useFullScreenStageOpen();
  const giverState = useEmpathyQueueStore((s) => s.giverState);
  const seekerState = useEmpathyQueueStore((s) => s.seekerState);
  return (
    isDeclared ||
    stageOpen ||
    isLiveSessionPath(pathname) ||
    QUIET_GIVER_STATES.has(giverState) ||
    (seekerState !== null && QUIET_SEEKER_STATES.has(seekerState))
  );
}

/** Test seam: forget every declaration. */
export function resetQuietZone(): void {
  declared = 0;
  emit();
}
